import { keyBy } from 'lodash'
import AccessControl from '@modules/auth/AccessControl'
import InputField from '@modules/formFields/InputField'
import PhoneField from '@modules/formFields/PhoneField'
import SelectCompanyField from '@modules/formFields/SelectCompanyField'
import SelectCountryField from '@modules/formFields/SelectCountryField'
import SelectLanguageField from '@modules/formFields/SelectLanguageField'
import SelectProviderField from '@modules/formFields/SelectProviderField'
import SelectSpecialityField from '@modules/formFields/SelectSpecialityField'
import TimeZoneSelector from '@modules/formFields/SelectTimeZoneField'
import { uploadImageComponent } from '@modules/formFields/uploadImageComponent'
import React from 'react'
import { Trans, withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Field, FieldArray, Form, formValueSelector, reduxForm, SubmissionError } from 'redux-form/dist/redux-form'
import Consultations from '@services/Consultations'
import { history } from '@services/History'
import SelectAppRolesField from '../../formFields/SelectAppRolesField'
import SelectField from '../../formFields/SelectField'
import SelectRoleField from '../../formFields/SelectRoleField'
import ProfessionalTranslationsField from './Professional.Form.Translations.Field'
import { ProfessionalValidation } from './Professional.Form.Validations'
import styled from 'styled-components'
import { PAAS_ADMIN, PROVIDER_ADMIN, SUPER_ADMIN } from '@constants/adminRoles'
import SuggestField from '@modules/admin/common/fields/suggestField'

let ProfessionalForm = ({
  initialValues,
  submitting,
  handleSubmit,
  error,
  submitFailed,
  isPrescriptor,
  professionalRoles,
  t,
  ...props
}) => {
  const clientProfessional = Consultations.professional

  const submit = (values) => {
    const requestBody = {
      id: values.id,
      nid_number: values.nid_number,
      name: values.name,
      email: values.email,
      password: values.password,
      phone: values.phone.phone,
      avatar: values.avatar,
      signature: values.signature,
      collegiate_number: values.collegiate_number,
      timezone: values.timezone,
      is_visible_offline: values.is_visible_offline ? 1 : 0,
      app_role_id: values.app_role_id,
      professional_type: values.professional_type || undefined,
      payer: values.payer,
      profile_ids: values?.profiles?.map((profile) => profile.id),
      provider_id: values.provider && values.provider.id,
      speciality_id: values.speciality && values.speciality.id,
      role_ids: values.roles && values.roles.map((role) => role.id),
      company_ids: values.companies && values.companies.map((role) => role.id),
      country_codes: values.countries && values.countries.map((country) => country.code),
      language_codes: values.languages && values.languages.map((language) => language.iso_code),
      teams_ids: values?.teams_ids?.map((team) => team.id),
      access_type: values.access_type,
      translations: values.translations && keyBy(values.translations, (el) => el.language.iso_code),
      can_prescribe: values.can_prescribe,
      firstname: values.can_prescribe ? values.firstname : undefined,
      surname: values.can_prescribe ? values.surname : undefined,
      lastname: values.can_prescribe ? values.lastname : undefined,
      validated_collegiate_number: values.can_prescribe ? values.validated_collegiate_number : undefined,
    }

    if (initialValues.id) {
      return clientProfessional
        .update(initialValues.id, requestBody)
        .then(() => history.push(`/professional/${initialValues.id}`))
        .catch((err) => {
          throw new SubmissionError({ ...err.response.data.errors, _error: { server: err.message } })
        })
    }

    return clientProfessional
      .store(requestBody)
      .then(() => history.push('/professional'))
      .catch((err) => {
        throw new SubmissionError({ ...err.response.data.errors, _error: { server: err.message } })
      })
  }

  return (
    <Form className="md-form" onSubmit={handleSubmit(submit)}>
      <div className="double-box">
        <div>
          <Field name="name" component={InputField} placeholder={t('Nombre')} label={<Trans>Nombre</Trans>} />
          <Field name="email" component={InputField} placeholder={t('Email')} label={<Trans>Email</Trans>} />
          <Field name="nid_number" component={InputField} placeholder={t('DNI/NIE')} label={<Trans>DNI/NIE</Trans>} />
          <Field name="phone" placeholder={t('Teléfono')} label={<Trans>Teléfono</Trans>} component={PhoneField} />
        </div>
        <div>
          <Field name={'avatar'} component={uploadImageComponent} label={<Trans>Avatar</Trans>} />
        </div>
      </div>
      <div className="double-box">
        <div>
          <Field
            name="collegiate_number"
            component={InputField}
            placeholder={t('Número de colegiado')}
            label={<Trans>Número de colegiado</Trans>}
          />
          <div className="md-field">
            <label>
              <Trans>Visible sin conexión</Trans>
            </label>
            <div className="flex-check">
              <Field name="is_visible_offline" type="checkbox" component="input" className="check-box" />
              <span>
                <Trans>Este usuario puede aparecer en la lista de contactos si está offline</Trans>
              </span>
            </div>
          </div>

          <div className="md-field">
            <label>Profesional con prescipción</label>
            <Field
              name="can_prescribe"
              type="checkbox"
              component="input"
              className="check-box"
              style={{ margin: '0 12px' }}
            />
          </div>
          {!!isPrescriptor && (
            <WrapperPrescriptions>
              <PrescriptionText>
                <Trans i18nKey="prescriptionInformationText">
                  Para poder usar el servicio de prescipción médica, deberás rellenar los siguientes campos del
                  formulario con <span>tus datos reales</span>. Estos datos{' '}
                  <span>no serán visibles para los pacientes</span> pero deberán ser los reales para poder recetar
                  medicamentos.
                  <span>
                    Asegúrate de introducir correctamente tus datos ya que no los vas a poder modificar una vez
                    validados
                  </span>
                </Trans>
              </PrescriptionText>

              <Field
                name="firstname"
                component={InputField}
                placeholder={t('Nombre')}
                label={<Trans>Nombre</Trans>}
                disabled={initialValues.firstname}
              />
              <Field
                name="surname"
                component={InputField}
                placeholder={'Primer Apellido'}
                label={'Primer apellido'}
                disabled={initialValues.surname}
              />
              <Field
                name="lastname"
                component={InputField}
                placeholder={'Segundo Apellido'}
                label={'Segundo apellido'}
                disabled={initialValues.lastname}
              />
              <Field
                name="validated_collegiate_number"
                component={InputField}
                type="number"
                placeholder={t('Número de colegiado')}
                label={<Trans>Número de colegiado</Trans>}
                disabled={initialValues.validated_collegiate_number}
              />
            </WrapperPrescriptions>
          )}
        </div>
        <div>
          <Field name={'signature'} component={uploadImageComponent} label={<Trans>Firma</Trans>} />
        </div>
      </div>
      <AccessControl roles={[SUPER_ADMIN, PAAS_ADMIN, PROVIDER_ADMIN]}>
        <Field
          name="professional_type"
          component={SelectField}
          options={[
            { id: '', name: '-' },
            { id: 'medical', name: t('professional.type.medical') },
            { id: 'veterinary', name: t('professional.type.veterinary') },
          ]}
          placeholder={t('professional.type.placeholder')}
          label={t('professional.type.label')}
          simpleValue
          disabled={initialValues.professional_type}
        />
        <Field
          name="profiles"
          component={SelectField}
          placeholder={t('rolWorkplace')}
          options={professionalRoles}
          label={t('rolWorkplace')}
          multi
        />
      </AccessControl>
      <AccessControl requiredRole="PaaS">
        <Field name="roles" component={SelectRoleField} label={<Trans>Roles</Trans>} multi />
        <Field
          name="app_role_id"
          simpleValue
          component={SelectAppRolesField}
          placeholder={t('App role')}
          label={<Trans>App role</Trans>}
        />
        <Field
          name="payer"
          component={SelectField}
          options={[
            { id: 'España', name: t('España') },
            { id: 'México', name: t('México') },
            { id: 'Dubai', name: t('Dubai') },
          ]}
          placeholder={t('subsidiaryPayProfessional')}
          label={<Trans>subsidiaryPayProfessional</Trans>}
          simpleValue
        />
        <Field
          name="access_type"
          component={SelectField}
          options={[
            { id: 'support', name: t('Soporte') },
            { id: 'medical', name: t('Medico') },
          ]}
          placeholder={t('Tipo de acceso')}
          label={<Trans>Tipo de acceso</Trans>}
          simpleValue
        />
      </AccessControl>

      <AccessControl>
        <Field
          name="companies"
          component={SelectCompanyField}
          placeholder={t('Compañías')}
          label={<Trans>Compañías</Trans>}
          multi
        />

        <Field
          name="provider"
          component={SelectProviderField}
          placeholder={t('Proveedor')}
          label={<Trans>Proveedor</Trans>}
        />
      </AccessControl>
      <Field
        name="speciality"
        component={SelectSpecialityField}
        placeholder={t('Especialidad')}
        label={<Trans>Especialidad</Trans>}
      />
      <Field
        name="teams_ids"
        component={SuggestField}
        client={(params) => Consultations.teams.index(params)}
        label={t('assignedProfessionalGroups')}
        placeholder={t('assignedProfessionalGroups')}
      />

      <Field
        name="timezone"
        component={TimeZoneSelector}
        placeholder={t('Zona horaria')}
        label={<Trans>Zona horaria / País</Trans>}
      />
      <Field
        name="countries"
        component={SelectCountryField}
        placeholder={t('Países')}
        label={<Trans>Países activos</Trans>}
        multi
      />
      <Field
        name="languages"
        component={SelectLanguageField}
        placeholder={t('Idiomas')}
        label={<Trans>Idiomas</Trans>}
        multi
      />
      <FieldArray name="translations" component={ProfessionalTranslationsField} />
      {error && submitFailed && (
        <div className="error">
          <div>{error.professionalForm}</div>
          <div>{error.spanishIsRequired}</div>
          <div>{error.translationArray}</div>
          <div>{error.server}</div>
        </div>
      )}
      <button type="submit" className="btn btn-primary" disabled={submitting}>
        <Trans>Guardar</Trans>
      </button>
    </Form>
  )
}

ProfessionalForm = reduxForm({ form: 'professional', validate: ProfessionalValidation })(ProfessionalForm)

const selector = formValueSelector('professional') // <-- same as form name
ProfessionalForm = connect((state) => {
  const isPrescriptor = selector(state, 'can_prescribe')
  return {
    isPrescriptor,
  }
})(ProfessionalForm)

export default withTranslation()(ProfessionalForm)
const PrescriptionText = styled.p`
  font-style: italic;
  span {
    font-weight: bold;
  }
`
const WrapperPrescriptions = styled.div`
  background: #ffffff;
  padding: 12px;
  margin-bottom: 12px;
  border-radius: 10px;
  label {
    color: #555;
  }
`
